import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X, Copy, Megaphone, Plus, Check, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { AdvanceNoticeIndicator } from '@/components/AdvanceNoticeIndicator';
import { PausedCampaignIndicator } from '@/components/PausedCampaignIndicator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { useOptimizedRecallList } from '../hooks/useRecallListQuery';
import useSelectedRecalls from '@/hooks/useSelectedRecalls';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import RecallStatusBadge from '@/components/RecallStatusBadge';
import { useRecallAssignmentStatus } from '@/hooks/useRecallAssignmentStatus';
import { RecallFilterBar, RecallFilterValues } from '../components/RecallFilterBar';
import { usePersistedFilters } from '../hooks/usePersistedFilters';
import { useTechniciansQuery } from '@/hooks/useTechniciansQuery';
import { getTechnicianColor as resolveTechnicianColor } from '../utils/technicianColors';
import { useCampaignPriorities } from '@/hooks/useCampaignPriorities';
import { translateSystemError, isSystemError, logSystemError } from '@/lib/utils/errorTranslator';
import { describeActionError } from '@/lib/errors/lifecycleRefusal';
import { useToast } from '@/hooks/use-toast';
import { InlineError } from '@/components/InlineError';
import { useTenant } from '@/contexts/TenantContext';
import { SecurityBoundary } from '@/lib/security';
import { logDataAccess, logAdminAction } from '@/lib/security/audit';
import { logUserActivity } from '@/lib/activity/userActivity';
import { PermissionScope } from '@/types';
import { RemoveRecallDialog, RemovalReason } from '@/components/RemoveRecallDialog';
import {
  statusForRemovalReason,
  reasonTextForRemoval
} from '@/lib/lifecycle/releaseDisposition';
import { updateLifecycleOnManualStatusChange } from '@/lib/supabase/recallLifecycle';
import { routeTrackingAPI } from '@/lib/supabase/routeTracking';
import { removeCache } from '@/lib/utils/cache';
import { useDealerPreferences } from '@/hooks/useDealerPreferences';

/* eslint-disable max-lines-per-function, complexity, max-len, @typescript-eslint/no-explicit-any, jsx-a11y/label-has-associated-control, react/no-array-index-key, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
const RecallListPage = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const { currentTenant, isLoading: tenantLoading } = useTenant();
  const tenantId = currentTenant?.id || null;

  const {
    recalls: rawRecalls,
    paginationInfo,
    filterOptions,
    loading,
    error,
    filterOptionsError,
    filters,
    currentPage,
    pageSize,
    updateFilters,
    clearFilters,
    goToPage,
    changePageSize
  } = useOptimizedRecallList(session?.user?.id, tenantId ?? '');

  const {
    addRecall,
    removeRecall,
    selectedRecalls: userSelectedRecalls,
    isAuthenticated,
    refreshData: refreshSelectedRecalls
  } = useSelectedRecalls();

  const { getRecallStatus } = useRecallAssignmentStatus();
  const { campaignStatus, campaignStatusReasons } = useCampaignPriorities(session?.user?.id);
  const { getDealerPreference } = useDealerPreferences(session?.user?.id);
  const { toast } = useToast();

  // State declarations first
  const [zipRadius, setZipRadius] = React.useState<string>('0');

  // Assignment-visibility filters (parity with the recall map)
  const [assignmentState, setAssignmentState] = React.useState<'all' | 'unassigned' | 'in_route'>(
    'all'
  );
  const [selectedTechnicianIds, setSelectedTechnicianIds] = React.useState<string[]>([]);

  // Sorting state
  const [orderBy, setOrderBy] = React.useState('campaign_number');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');

  // Error state for inline error display
  const [actionErrors, setActionErrors] = React.useState<Record<string, string>>({});

  // Helper function to show success toasts (keep for non-blocking notifications)
  const showSuccessToast = (message: string) => {
    toast({
      title: 'Success',
      description: message,
      variant: 'success'
    });
  };

  // Get unique options for filter dropdowns (now efficiently loaded via React Query)
  const uniqueCampaigns = React.useMemo(() => {
    if (filterOptions?.campaigns && filterOptions.campaigns.length > 0) {
      return filterOptions.campaigns;
    }
    // Fallback: derive from loaded recalls when filter options not yet available
    if (!rawRecalls.length) return [];
    const campaigns = new Set(rawRecalls.map(r => r.campaign_number).filter(Boolean));
    return Array.from(campaigns).sort();
  }, [filterOptions?.campaigns, rawRecalls]);

  const uniqueCities = React.useMemo(() => {
    return filterOptions?.cities || [];
  }, [filterOptions]);

  const uniqueZipCodes = React.useMemo(() => {
    return filterOptions?.zipCodes || [];
  }, [filterOptions]);

  // Custom clear function that also clears search terms
  const handleClearFilters = () => {
    clearFilters();
    setZipRadius('0');
    setSearchTerm('');
    setAssignmentState('all');
    setSelectedTechnicianIds([]);
  };

  // Helper functions to count active filters in each group
  const getVehicleFilterCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filters.make) count++;
    if (filters.model) count++;
    if (filters.year) count++;
    return count;
  };

  const getLocationFilterCount = () => {
    let count = 0;
    if (filters.dealer) count++;
    if (filters.location?.city) count++;
    if (filters.location?.zipCode) count++;
    if (zipRadius !== '0') count++;
    return count;
  };

  const getRecallFilterCount = () => {
    let count = 0;
    if (filters.campaigns?.length) count++;
    if (filters.priority && filters.priority.toString() !== 'all') count++;
    if (filters.mobile_eligible && filters.mobile_eligible.toString() !== 'all') count++;
    if ((filters as any).campaign_status && (filters as any).campaign_status !== 'all') count++;
    return count;
  };

  // Refresh selected recalls when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page is visible again, refresh selected recalls
        refreshSelectedRecalls();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshSelectedRecalls]);

  // Log page view on mount
  useEffect(() => {
    logUserActivity({
      category: 'recall_selection',
      action: 'view_recalls'
    });
  }, []);

  // Auto-refresh selected recalls every 30 seconds while page is visible
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        refreshSelectedRecalls();
      }
    }, 30000); // Reduced from 3s to 30s

    return () => clearInterval(interval);
  }, [refreshSelectedRecalls]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecalls, setSelectedRecalls] = useState<Set<string>>(new Set());
  const [addingToRoute, setAddingToRoute] = useState<Set<string>>(new Set());
  const [removalDialogOpen, setRemovalDialogOpen] = useState(false);
  const [recallToRemove, setRecallToRemove] = useState<any | null>(null);
  const [removedRecalls, setRemovedRecalls] = useState<Set<string>>(new Set());
  const [fadingOutRecalls, setFadingOutRecalls] = useState<Set<string>>(new Set());

  // Apply lightweight client-side filtering and sorting only
  const { recalls, filteredTotal } = React.useMemo(() => {
    let filteredRecalls = rawRecalls;
    let totalFiltered = paginationInfo?.total || 0;
    let dealerFilteringApplied = false;

    // Filter out excluded dealers (same exclusion the map page applies)
    if (getDealerPreference) {
      const beforeCount = filteredRecalls.length;
      filteredRecalls = filteredRecalls.filter(recall => {
        const dealerName = (recall as any).seller_name?.trim() || '';
        if (!dealerName) return true;
        const pref = getDealerPreference(dealerName);
        return pref.is_included !== false;
      });
      // Only mark dealer filtering as applied if it actually excluded records
      if (filteredRecalls.length !== beforeCount) {
        dealerFilteringApplied = true;
        totalFiltered = filteredRecalls.length;
      }
    }

    // Apply campaign status filtering (requires campaign status lookup)
    if ((filters as any).campaign_status && (filters as any).campaign_status !== 'all') {
      filteredRecalls = filteredRecalls.filter(recall => {
        if (!recall.campaign_number) return false;
        const campaignLevelStatus = campaignStatus[recall.campaign_number] || 'Available';

        // Recall-level advance notice upgrades "Available" → "Advance Notice",
        // but does NOT override user-set "Paused"
        const effectiveStatus =
          recall.has_advance_notice_recalls && campaignLevelStatus !== 'Paused'
            ? 'Advance Notice'
            : campaignLevelStatus;

        return effectiveStatus === (filters as any).campaign_status;
      });
      totalFiltered = filteredRecalls.length;
    }

    // Apply priority/skill level filtering (uses campaign configuration skill levels)
    if (filters.priority && filters.priority.toString() !== 'all') {
      filteredRecalls = filteredRecalls.filter(recall => {
        const recallPriority = recall.priority || 3; // Default to low (3) if not set
        return recallPriority.toString() === filters.priority?.toString();
      });
      totalFiltered = filteredRecalls.length;
    }

    // Apply mobile eligible filtering
    if (filters.mobile_eligible !== undefined && filters.mobile_eligible.toString() !== 'all') {
      const isMobileEligible = filters.mobile_eligible.toString() === 'true';
      filteredRecalls = filteredRecalls.filter(recall => {
        return (recall as any).mobile_eligible === isMobileEligible;
      });
      totalFiltered = filteredRecalls.length;
    }

    // Apply assignment-state filtering (unassigned vs in a route / handled)
    if (assignmentState !== 'all') {
      filteredRecalls = filteredRecalls.filter(recall => {
        const info = getRecallStatus(recall.vin, recall.campaign_number);
        const inRoute = !!info.routeId;
        const assignedTech = info.status !== 'available' && !!info.technicianId;
        const staged = selectedRecalls.has(`${recall.vin}-${recall.campaign_number}`);
        const handled = inRoute || assignedTech || staged;
        if (assignmentState === 'unassigned') return !handled;
        if (assignmentState === 'in_route') return handled;
        return true;
      });
      totalFiltered = filteredRecalls.length;
    }

    // Apply technician filtering
    if (selectedTechnicianIds.length > 0) {
      filteredRecalls = filteredRecalls.filter(recall => {
        const info = getRecallStatus(recall.vin, recall.campaign_number);
        return info.technicianId ? selectedTechnicianIds.includes(info.technicianId) : false;
      });
      totalFiltered = filteredRecalls.length;
    }

    // Apply sorting (lightweight operation)
    filteredRecalls.sort((a, b) => {
      let aValue = (a as any)[orderBy];
      let bValue = (b as any)[orderBy];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return order === 'asc' ? 1 : -1;
      if (bValue == null) return order === 'asc' ? -1 : 1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });

    // Client-side pagination only when client-side filtering actually modified the data
    // Note: dealerFilteringApplied is true only if dealers were actually excluded
    // This prevents double-pagination when server already returns paginated results
    if (
      dealerFilteringApplied ||
      ((filters as any).campaign_status && (filters as any).campaign_status !== 'all') ||
      assignmentState !== 'all' ||
      selectedTechnicianIds.length > 0 ||
      (filters.priority && filters.priority.toString() !== 'all') ||
      (filters.mobile_eligible !== undefined && filters.mobile_eligible.toString() !== 'all')
    ) {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      filteredRecalls = filteredRecalls.slice(startIndex, endIndex);
    }

    return { recalls: filteredRecalls, filteredTotal: totalFiltered };
  }, [
    rawRecalls,
    campaignStatus,
    getDealerPreference,
    filters.priority,
    filters.mobile_eligible,
    assignmentState,
    selectedTechnicianIds,
    selectedRecalls,
    (filters as any).campaign_status,
    getRecallStatus,
    currentPage,
    pageSize,
    paginationInfo?.total,
    orderBy,
    order
  ]);

  // Update pagination info for client-side filtering
  const effectivePaginationInfo = React.useMemo(() => {
    if (
      assignmentState !== 'all' ||
      selectedTechnicianIds.length > 0 ||
      ((filters as any).campaign_status && (filters as any).campaign_status !== 'all') ||
      (filters.priority && filters.priority.toString() !== 'all')
    ) {
      // Use client-side filtered total only for filters that can't be done server-side
      const totalPages = Math.ceil(filteredTotal / pageSize);
      const startIndex = (currentPage - 1) * pageSize + 1;
      const endIndex = Math.min(currentPage * pageSize, filteredTotal);

      return {
        startIndex,
        endIndex,
        total: filteredTotal,
        page: currentPage,
        totalPages,
        pageSize
      };
    }
    return paginationInfo;
  }, [
    paginationInfo,
    filteredTotal,
    currentPage,
    pageSize,
    assignmentState,
    selectedTechnicianIds,
    (filters as any).campaign_status
  ]);

  // Column widths state for resizable columns
  const [columnWidths, setColumnWidths] = useState({
    vin: 140, // Reduced from 160
    campaign: 100, // Reduced from 120
    description: 300, // Reduced from 420 to make room for other columns
    year: 60, // Reduced from 70
    make: 80, // Reduced from 100
    model: 100, // Reduced from 120
    dealer: 140, // Reduced from 180
    address: 160, // Reduced from 200
    city: 100, // Reduced from 120
    zip: 70, // Reduced from 80
    route: 100 // Increased from 60 to ensure visibility
  });

  const resizingColumn = useRef<string | null>(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (value.trim()) {
      updateFilters({ vin: value.trim() });
    } else {
      updateFilters({ vin: undefined });
    }
  };

  // Handle sorting
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrderBy(property);
    setOrder(isAsc ? 'desc' : 'asc');
  };

  // Get sort icon for column headers
  const getSortIcon = (property: string) => {
    if (orderBy !== property) {
      return <ArrowUpDown className='h-4 w-4' />;
    }
    return order === 'asc' ? <ArrowUp className='h-4 w-4' /> : <ArrowDown className='h-4 w-4' />;
  };

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    if (value === 'all' || value === '') {
      updateFilters({ [key]: undefined });
    } else {
      // Convert priority to number if it's a numeric string
      if (key === 'priority' && !isNaN(Number(value))) {
        updateFilters({ [key]: Number(value) });
      } else {
        updateFilters({ [key]: value });
      }
    }
  };

  // Technician roster + stable colors for the filter bar
  const { data: techniciansData } = useTechniciansQuery();
  const technicians = React.useMemo(
    () => (techniciansData?.technicians || []) as Array<{ id: string; full_name: string }>,
    [techniciansData]
  );
  const getTechnicianColor = React.useCallback(
    (id: string) => resolveTechnicianColor(id, technicians),
    [technicians]
  );

  // Adapter: present the list's nested filter state as the flat shape the
  // shared RecallFilterBar expects.
  const filterBarValues: RecallFilterValues = {
    vin: searchTerm,
    make: ((filters as any).make as string) || '',
    model: ((filters as any).model as string) || '',
    year: (filters as any).year ? String((filters as any).year) : '',
    dealer: ((filters as any).dealer as string) || '',
    campaigns: filters.campaigns || [],
    city: filters.location?.city || 'all',
    zip: filters.location?.zipCode || 'all',
    mobile_eligible:
      filters.mobile_eligible !== undefined ? String(filters.mobile_eligible) : 'all',
    campaign_status: (filters as any).campaign_status || 'all',
    priority: filters.priority !== undefined ? String(filters.priority) : 'all',
    assignment_state: assignmentState
  };

  const handleFilterBarChange = (patch: Partial<RecallFilterValues>) => {
    if (patch.vin !== undefined) handleSearch(patch.vin);
    if (patch.make !== undefined) handleFilterChange('make', patch.make);
    if (patch.model !== undefined) handleFilterChange('model', patch.model);
    if (patch.year !== undefined) handleFilterChange('year', patch.year);
    if (patch.dealer !== undefined) handleFilterChange('dealer', patch.dealer);
    if (patch.campaigns !== undefined) {
      updateFilters({ campaigns: patch.campaigns.length ? patch.campaigns : undefined });
    }
    if (patch.city !== undefined) {
      updateFilters({
        location: { ...filters.location, city: patch.city === 'all' ? undefined : patch.city }
      });
    }
    if (patch.zip !== undefined) {
      updateFilters({
        location: { ...filters.location, zipCode: patch.zip === 'all' ? undefined : patch.zip }
      });
    }
    if (patch.mobile_eligible !== undefined) {
      handleFilterChange('mobile_eligible', patch.mobile_eligible);
    }
    if (patch.campaign_status !== undefined) {
      handleFilterChange('campaign_status', patch.campaign_status);
    }
    if (patch.priority !== undefined) handleFilterChange('priority', patch.priority);
    if (patch.assignment_state !== undefined) {
      setAssignmentState(patch.assignment_state as typeof assignmentState);
    }
  };

  // Persist structural filters per tenant across sessions (free-text excluded).
  const filterPrefsSnapshot = React.useMemo(
    () => ({
      city: filters.location?.city || 'all',
      zip: filters.location?.zipCode || 'all',
      zipRadius,
      campaigns: filters.campaigns || [],
      mobile_eligible:
        filters.mobile_eligible !== undefined ? String(filters.mobile_eligible) : 'all',
      campaign_status: (filters as any).campaign_status || 'all',
      priority: filters.priority !== undefined ? String(filters.priority) : 'all',
      assignment_state: assignmentState,
      selectedTechnicianIds
    }),
    [
      filters.location?.city,
      filters.location?.zipCode,
      filters.campaigns,
      filters.mobile_eligible,
      filters.priority,
      zipRadius,
      assignmentState,
      selectedTechnicianIds
    ]
  );

  const restoreFilterPrefs = React.useCallback(
    (restored: Partial<typeof filterPrefsSnapshot>) => {
      const updates: any = {};
      if (restored.campaigns !== undefined) {
        updates.campaigns = restored.campaigns.length ? restored.campaigns : undefined;
      }
      if (restored.mobile_eligible !== undefined) {
        updates.mobile_eligible =
          restored.mobile_eligible === 'all' ? undefined : restored.mobile_eligible;
      }
      if (restored.campaign_status !== undefined) {
        updates.campaign_status =
          restored.campaign_status === 'all' ? undefined : restored.campaign_status;
      }
      if (restored.priority !== undefined && restored.priority !== 'all') {
        updates.priority = isNaN(Number(restored.priority))
          ? restored.priority
          : Number(restored.priority);
      }
      const loc: { city?: string; zipCode?: string } = {};
      if (restored.city !== undefined && restored.city !== 'all') loc.city = restored.city;
      if (restored.zip !== undefined && restored.zip !== 'all') loc.zipCode = restored.zip;
      if (Object.keys(loc).length) updates.location = loc;
      if (Object.keys(updates).length) updateFilters(updates);
      if (restored.zipRadius !== undefined) setZipRadius(restored.zipRadius);
      if (restored.assignment_state !== undefined) {
        setAssignmentState(restored.assignment_state as typeof assignmentState);
      }
      if (restored.selectedTechnicianIds !== undefined) {
        setSelectedTechnicianIds(restored.selectedTechnicianIds);
      }
    },
    [updateFilters]
  );

  usePersistedFilters('recall_list', filterPrefsSnapshot, restoreFilterPrefs);

  // Update selected recalls when user's selected recalls change
  useEffect(() => {
    if (!userSelectedRecalls.length) {
      setSelectedRecalls(new Set());
      return;
    }

    const newSelectedRecalls = new Set<string>();
    for (const selectedRecall of userSelectedRecalls) {
      const key = `${selectedRecall.vin}-${selectedRecall.campaign_number}`;
      newSelectedRecalls.add(key);
    }

    setSelectedRecalls(newSelectedRecalls);
  }, [userSelectedRecalls]);

  // Handle removing recall from view (completing lifecycle)
  const handleRemoveRecallClick = (recall: any, event: React.MouseEvent) => {
    event.stopPropagation();

    // Security check: Ensure user is authenticated
    if (!session?.user?.id) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to remove recalls',
        variant: 'default'
      });
      return;
    }

    setRecallToRemove(recall);
    setRemovalDialogOpen(true);
  };

  const handleConfirmRemoval = async (reason: RemovalReason, otherReason?: string) => {
    if (!recallToRemove || !session?.user?.id || !tenantId) {
      toast({
        title: 'Error',
        description: 'Missing required information to remove recall',
        variant: 'destructive'
      });
      return;
    }

    const recallKey = `${recallToRemove.vin}-${recallToRemove.campaign_number}`;
    const assignmentStatus = getRecallStatus(recallToRemove.vin, recallToRemove.campaign_number);

    // Capture old state for audit logging
    const oldState = {
      vin: recallToRemove.vin,
      campaign_number: recallToRemove.campaign_number,
      assignment_status: assignmentStatus?.status || 'available',
      assignment_id: assignmentStatus?.assignmentId || null
    };

    try {
      // Map removal reason to final status
      // One shared mapping for both pages. They used to decide this
      // separately and disagreed: the list sent `not_available`, the map
      // sent `not_there`, from the same click.
      const finalStatus = statusForRemovalReason(reason);
      const reasonText = reasonTextForRemoval(reason, otherReason);

      // If recall has an assignment, update via routeTrackingAPI
      if (assignmentStatus?.assignmentId) {
        const result = await routeTrackingAPI.updateRecallStatuses([
          {
            assignmentId: assignmentStatus.assignmentId,
            status: finalStatus,
            reason: reasonText
          }
        ]);

        if (!result.success) {
          throw new Error(result.error || 'Failed to update recall status');
        }

        // Audit log: Data modification for assigned recall
        logAdminAction(
          'recall_status_update',
          {
            resource: 'recall',
            resource_id: recallKey,
            assignment_id: assignmentStatus.assignmentId,
            vin: recallToRemove.vin,
            campaign_number: recallToRemove.campaign_number
          },
          {
            old_status: oldState.assignment_status,
            new_status: finalStatus,
            reason: reason,
            other_reason: otherReason,
            tenant_id: tenantId,
            timestamp: new Date().toISOString()
          }
        ).catch(err => console.warn('Audit logging failed (non-blocking):', err));
      } else {
        // If no assignment, create lifecycle record directly
        try {
          const { getProdSupabaseClient } = await import('@/lib/supabase/client');
          const { getTenantIdFromSession } = await import('@/lib/supabase/tenantScoping');
          const prodClient = getProdSupabaseClient();
          const resolvedTenantId = await getTenantIdFromSession(prodClient);

          if (!resolvedTenantId) {
            throw new Error('Unable to determine tenant association');
          }

          // Security: Verify tenant matches context tenant (defense in depth)
          if (tenantId && resolvedTenantId !== tenantId) {
            // eslint-disable-next-line no-console
            console.error('Tenant mismatch detected:', {
              contextTenantId: tenantId,
              sessionTenantId: resolvedTenantId,
              vin: recallToRemove.vin
            });
            throw new Error('Tenant verification failed');
          }

          // Guarded write: cannot move a recall out of a terminal state, cannot
          // lose a concurrent update, and its errors are logged. This replaces a
          // direct check-then-act insert/update that bypassed the guard entirely.
          await updateLifecycleOnManualStatusChange(
            recallToRemove.vin,
            recallToRemove.campaign_number,
            { finalStatus, ...(reasonText ? { otherReason: reasonText } : {}) }
          );

          // Audit log: Data modification for unassigned recall
          logAdminAction(
            'recall_lifecycle_update',
            {
              resource: 'recall',
              resource_id: recallKey,
              vin: recallToRemove.vin,
              campaign_number: recallToRemove.campaign_number
            },
            {
              old_status: 'available',
              new_status: finalStatus,
              reason: reason,
              other_reason: otherReason,
              tenant_id: resolvedTenantId,
              timestamp: new Date().toISOString()
            }
          ).catch(err => console.warn('Audit logging failed (non-blocking):', err));
        } catch (lifecycleError) {
          // eslint-disable-next-line no-console
          console.error('Error creating lifecycle record:', lifecycleError);
          // Log security violation if tenant scoping fails
          const { logSecurityViolation } = await import('@/lib/security/audit');
          logSecurityViolation('tenant_scoping_failure', {
            error: lifecycleError instanceof Error ? lifecycleError.message : 'Unknown error',
            vin: recallToRemove.vin,
            campaign_number: recallToRemove.campaign_number,
            tenant_id: tenantId
          }).catch(() => {
            // Ignore audit logging errors
          });
          throw lifecycleError;
        }
      }

      // Invalidate recall list cache so refetch excludes the removed recall
      queryClient.invalidateQueries({ queryKey: ['recalls'] });

      // Clear map caches so completed_recalls and raw data are refreshed on map page
      if (tenantId) {
        removeCache('user_preferences', tenantId);
        removeCache('raw_recalls', tenantId);
      }

      // Close dialog immediately (instant feedback pattern)
      setRemovalDialogOpen(false);
      toast({
        title: 'Recall Removed',
        description: 'The recall has been removed from the view.',
        variant: 'success'
      });

      // Start fade-out animation
      setFadingOutRecalls(prev => new Set([...prev, recallKey]));

      // After animation completes, remove from display
      setTimeout(() => {
        setRemovedRecalls(prev => new Set([...prev, recallKey]));
        setFadingOutRecalls(prev => {
          const newSet = new Set(prev);
          newSet.delete(recallKey);
          return newSet;
        });
      }, 300); // Match fade-out duration
    } catch (error) {
      logSystemError(error, 'removing recall');
      const userMessage = isSystemError(error)
        ? translateSystemError(error, 'removing recall').message
        : 'Failed to remove recall';
      toast({
        title: 'Error',
        description: userMessage,
        variant: 'destructive'
      });
    } finally {
      setRecallToRemove(null);
    }
  };

  // Handle adding to route
  const handleAddToRoute = async (recall: any) => {
    const recallKey = `${recall.vin}-${recall.campaign_number}`;

    if (!isAuthenticated) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to add recalls to your route',
        variant: 'default'
      });
      return;
    }

    try {
      setAddingToRoute(prev => new Set([...prev, recallKey]));
      // Clear any previous error for this recall
      setActionErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[recallKey];
        return newErrors;
      });
      const result = await addRecall(recall);
      setSelectedRecalls(prev => new Set([...prev, recallKey]));
      // Refresh the selected recalls data to ensure UI shows correct state
      await refreshSelectedRecalls();
      showSuccessToast(
        result ? 'Recall added to route successfully' : 'Recall was already in route'
      );
    } catch (error: any) {
      logSystemError(error, 'adding recall to route');
      const userMessage = isSystemError(error)
        ? translateSystemError(error, 'adding recall to route').message
        : error.message || 'Failed to add recall to route';
      // Set inline error for this recall
      setActionErrors(prev => ({
        ...prev,
        [recallKey]: userMessage
      }));
    } finally {
      setAddingToRoute(prev => {
        const newSet = new Set(prev);
        newSet.delete(recallKey);
        return newSet;
      });
    }
  };

  // Handle removing from route
  const handleRemoveFromRoute = async (recall: any) => {
    const recallKey = `${recall.vin}-${recall.campaign_number}`;

    if (!isAuthenticated) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to remove recalls from your route',
        variant: 'default'
      });
      return;
    }

    try {
      setAddingToRoute(prev => new Set([...prev, recallKey]));
      // Clear any previous error for this recall
      setActionErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[recallKey];
        return newErrors;
      });
      await removeRecall(recall.vin, recall.campaign_number);
      setSelectedRecalls(prev => {
        const newSet = new Set(prev);
        newSet.delete(recallKey);
        return newSet;
      });
      showSuccessToast('Recall removed from route successfully');
    } catch (error: any) {
      logSystemError(error, 'removing recall from route');
      const { title, description } = describeActionError(error, 'removing this recall');
      // The inline slot in the desktop table is clamped to ~100px, so a full sentence is
      // unreadable there. Mark the row with the short title and put the explanation —
      // including who completed it and when — in the toast.
      setActionErrors(prev => ({
        ...prev,
        [recallKey]: title
      }));
      toast({ title, description, variant: 'destructive' });
    } finally {
      setAddingToRoute(prev => {
        const newSet = new Set(prev);
        newSet.delete(recallKey);
        return newSet;
      });
    }
  };

  // Column resize handlers
  const handleMouseDown = (columnKey: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    resizingColumn.current = columnKey;
    startX.current = e.clientX;
    startWidth.current = columnWidths[columnKey as keyof typeof columnWidths];

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Prevent text selection during resize
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!resizingColumn.current) return;

    e.preventDefault();
    const diff = e.clientX - startX.current;
    const newWidth = Math.max(50, startWidth.current + diff);
    const columnKey = resizingColumn.current;

    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: newWidth
    }));
  };

  const handleMouseUp = () => {
    resizingColumn.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    // Restore default cursor and text selection
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    if (!effectivePaginationInfo) return [];

    const { page, totalPages } = effectivePaginationInfo;
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, page - 2);
      const end = Math.min(totalPages, start + maxVisible - 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  // Log data access when recalls are loaded
  React.useEffect(() => {
    if (recalls.length > 0 && !loading) {
      logDataAccess({
        resource: 'recalls',
        action: 'view',
        count: recalls.length,
        filters: filters,
        metadata: {
          page: currentPage,
          pageSize: pageSize,
          total: paginationInfo?.total || 0
        }
      }).catch(() => {
        // Silently fail audit logging
      });
    }
  }, [recalls.length, loading, currentPage, pageSize, paginationInfo?.total, filters]);

  // Tenant ID is mandatory - show loading while tenant context loads, error if not available
  if (tenantLoading) {
    return (
      <div className='w-full space-y-4 md:space-y-6'>
        <div className='flex items-center justify-center py-12'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
          <span className='ml-2'>Loading tenant context...</span>
        </div>
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div className='w-full space-y-4 md:space-y-6'>
        <div className='flex items-center justify-center py-12'>
          <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
            <p className='text-red-800'>Error: Tenant context is required to view recalls.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && rawRecalls.length === 0) {
    return (
      <div className='w-full space-y-4 md:space-y-6'>
        <div className='flex items-center justify-center py-12'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
          <span className='ml-2'>Loading recalls...</span>
        </div>
      </div>
    );
  }

  if (error) {
    const isRateLimitError = error.includes('Rate limit exceeded');
    return (
      <div className='w-full space-y-4 md:space-y-6'>
        <div className='text-center py-12'>
          <div className='bg-destructive/10 border border-destructive/20 rounded-lg p-6 max-w-md mx-auto'>
            <h3 className='text-lg font-semibold text-destructive mb-2'>
              {isRateLimitError ? 'Too Many Requests' : 'Error Loading Recalls'}
            </h3>
            <p className='text-destructive/90'>
              {isRateLimitError
                ? "You've made too many requests. Please wait a moment before trying again."
                : error}
            </p>
            {filterOptionsError && (
              <p className='text-destructive/70 text-sm mt-2'>
                Filter options error: {filterOptionsError}
              </p>
            )}
            {isRateLimitError && (
              <Button
                variant='outline'
                size='sm'
                onClick={() => window.location.reload()}
                className='mt-4'
              >
                Try Again
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <SecurityBoundary
      requiredPermissions={[{ resource: 'recalls', action: 'read', scope: PermissionScope.SELF }]}
      auditPageAccess='recall-list'
      tenantIsolation={true}
    >
      <div className='w-full h-full flex flex-col space-y-3'>
        {/* Compact persistent filter bar (replaces header + filter panel) */}
        <RecallFilterBar
          values={filterBarValues}
          onChange={handleFilterBarChange}
          zipRadius={zipRadius}
          onZipRadiusChange={setZipRadius}
          selectedTechnicianIds={selectedTechnicianIds}
          onTechniciansChange={setSelectedTechnicianIds}
          colorByTechnician={false}
          onColorByTechnicianChange={() => undefined}
          showColorByTechnician={false}
          campaignsOptions={uniqueCampaigns}
          cityOptions={uniqueCities}
          zipOptions={uniqueZipCodes}
          technicians={technicians}
          getTechnicianColor={getTechnicianColor}
          activeFilterCount={
            getVehicleFilterCount() + getLocationFilterCount() + getRecallFilterCount()
          }
          onClearAll={handleClearFilters}
          statsText={`${effectivePaginationInfo?.total || 0} recalls`}
        />

        {/* Compact Actions & Results Section */}
        <div className='flex items-center justify-between px-1'>
          <div className='flex items-center gap-3'>
            <div className='text-sm text-muted-foreground'>
              <span className='font-medium'>{effectivePaginationInfo?.startIndex || 0}</span>-
              <span className='font-medium'>{effectivePaginationInfo?.endIndex || 0}</span> of{' '}
              <span className='font-medium'>{effectivePaginationInfo?.total || 0}</span>
            </div>
            <div className='text-sm text-muted-foreground'>
              Page <span className='font-medium'>{effectivePaginationInfo?.page || 0}</span>/
              <span className='font-medium'>{effectivePaginationInfo?.totalPages || 0}</span>
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <div className='flex items-center gap-1'>
              <label className='text-sm text-muted-foreground'>Show:</label>
              <Select
                value={pageSize.toString()}
                onValueChange={value => changePageSize(parseInt(value))}
              >
                <SelectTrigger className='w-16 h-8 text-sm'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='10'>10</SelectItem>
                  <SelectItem value='25'>25</SelectItem>
                  <SelectItem value='50'>50</SelectItem>
                  <SelectItem value='100'>100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Desktop Table with Resizable Columns */}
        <div className='flex-1 border rounded-lg bg-card overflow-hidden relative z-20'>
          <div className='h-full overflow-auto'>
            {/* Mobile: Horizontal scroll container */}
            <div className='overflow-x-auto -mx-2 sm:mx-0'>
              <div className='inline-block min-w-full align-middle sm:block'>
                <Table className='w-full min-w-[1350px]' style={{ tableLayout: 'fixed' }}>
                  <TableHeader className='sticky top-0 bg-muted z-10'>
                    <TableRow>
                      <TableHead
                        style={{ width: `${columnWidths.vin}px` }}
                        className='relative p-2 h-10 group'
                      >
                        <div className='flex items-center justify-between'>
                          <span className='text-sm font-medium'>VIN</span>
                          <div
                            className='absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors'
                            onMouseDown={e => handleMouseDown('vin', e)}
                          />
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ width: `${columnWidths.campaign}px` }}
                        className='relative p-2 h-10 group'
                      >
                        <div className='flex items-center justify-between'>
                          <button
                            className='flex items-center gap-1 text-sm font-medium hover:text-primary transition-colors'
                            onClick={() => handleSort('campaign_number')}
                          >
                            Campaign
                            {getSortIcon('campaign_number')}
                          </button>
                          <div
                            className='absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors'
                            onMouseDown={e => handleMouseDown('campaign', e)}
                          />
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ width: `${columnWidths.description}px`, minWidth: 0 }}
                        className='relative p-2 h-10 group'
                      >
                        <div className='flex items-center justify-between'>
                          <span className='text-sm font-medium'>Description</span>
                          <div
                            className='absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors'
                            onMouseDown={e => handleMouseDown('description', e)}
                          />
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ width: `${columnWidths.year}px` }}
                        className='relative p-2 h-10 group'
                      >
                        <div className='flex items-center justify-between'>
                          <button
                            className='flex items-center gap-1 text-sm font-medium hover:text-primary transition-colors'
                            onClick={() => handleSort('year')}
                          >
                            Year
                            {getSortIcon('year')}
                          </button>
                          <div
                            className='absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors'
                            onMouseDown={e => handleMouseDown('year', e)}
                          />
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ width: `${columnWidths.make}px` }}
                        className='relative p-2 h-10 group'
                      >
                        <div className='flex items-center justify-between'>
                          <button
                            className='flex items-center gap-1 text-sm font-medium hover:text-primary transition-colors'
                            onClick={() => handleSort('make')}
                          >
                            Make
                            {getSortIcon('make')}
                          </button>
                          <div
                            className='absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors'
                            onMouseDown={e => handleMouseDown('make', e)}
                          />
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ width: `${columnWidths.model}px` }}
                        className='relative p-2 h-10 group'
                      >
                        <div className='flex items-center justify-between'>
                          <button
                            className='flex items-center gap-1 text-sm font-medium hover:text-primary transition-colors'
                            onClick={() => handleSort('model')}
                          >
                            Model
                            {getSortIcon('model')}
                          </button>
                          <div
                            className='absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors'
                            onMouseDown={e => handleMouseDown('model', e)}
                          />
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ width: `${columnWidths.dealer}px` }}
                        className='relative p-2 h-10 group'
                      >
                        <div className='flex items-center justify-between'>
                          <button
                            className='flex items-center gap-1 text-sm font-medium hover:text-primary transition-colors'
                            onClick={() => handleSort('seller_name')}
                          >
                            Dealer
                            {getSortIcon('seller_name')}
                          </button>
                          <div
                            className='absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors'
                            onMouseDown={e => handleMouseDown('dealer', e)}
                          />
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ width: `${columnWidths.address}px` }}
                        className='relative p-2 h-10 group'
                      >
                        <div className='flex items-center justify-between'>
                          <span className='text-sm font-medium'>Address</span>
                          <div
                            className='absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors'
                            onMouseDown={e => handleMouseDown('address', e)}
                          />
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ width: `${columnWidths.city}px` }}
                        className='relative p-2 h-10 group'
                      >
                        <div className='flex items-center justify-between'>
                          <button
                            className='flex items-center gap-1 text-sm font-medium hover:text-primary transition-colors'
                            onClick={() => handleSort('dealer_city')}
                          >
                            City
                            {getSortIcon('dealer_city')}
                          </button>
                          <div
                            className='absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors'
                            onMouseDown={e => handleMouseDown('city', e)}
                          />
                        </div>
                      </TableHead>
                      <TableHead
                        style={{ width: `${columnWidths.zip}px` }}
                        className='relative p-2 h-10 group'
                      >
                        <div className='flex items-center justify-between'>
                          <button
                            className='flex items-center gap-1 text-sm font-medium hover:text-primary transition-colors'
                            onClick={() => handleSort('dealer_postal_code')}
                          >
                            ZIP
                            {getSortIcon('dealer_postal_code')}
                          </button>
                          <div
                            className='absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors'
                            onMouseDown={e => handleMouseDown('zip', e)}
                          />
                        </div>
                      </TableHead>
                      <TableHead style={{ width: `${columnWidths.route}px` }} className='p-2 h-10'>
                        <span className='text-sm font-medium'>Route</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recalls.map((recall, index) => {
                      const recallKey = `${recall.vin}-${recall.campaign_number}`;
                      const isRemoved = removedRecalls.has(recallKey);
                      const isFadingOut = fadingOutRecalls.has(recallKey);

                      // Skip rendering if removed
                      if (isRemoved) {
                        return null;
                      }

                      return (
                        <TableRow
                          key={`${recall.vin}-${recall.campaign_number}-${index}`}
                          className={`h-11 transition-opacity duration-300 ${
                            isFadingOut ? 'opacity-0' : 'opacity-100'
                          }`}
                        >
                          <TableCell
                            style={{ width: `${columnWidths.vin}px` }}
                            className='p-3 font-mono text-sm relative'
                          >
                            <span
                              className='truncate cursor-pointer block'
                              title={`${recall.vin} - Click to copy`}
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(recall.vin);
                                  showSuccessToast('VIN copied to clipboard!');
                                } catch (error) {
                                  // Copy errors are non-critical, keep as toast
                                  toast({
                                    title: 'Error',
                                    description: 'Failed to copy VIN to clipboard',
                                    variant: 'destructive'
                                  });
                                }
                              }}
                            >
                              {recall.vin}
                            </span>
                          </TableCell>

                          <TableCell
                            style={{ width: `${columnWidths.campaign}px` }}
                            className='p-3'
                          >
                            <div className='flex items-center gap-1'>
                              <Badge
                                variant='outline'
                                className='text-xs shrink-0 bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30 dark:border-blue-400/30 inline-flex items-center gap-1'
                              >
                                <Megaphone className='h-3.5 w-3.5' />
                                <span className='truncate'>
                                  {recall.campaign_number?.split('/')[0] || recall.campaign_number}
                                </span>
                                <AdvanceNoticeIndicator
                                  hasAdvanceNotice={(() => {
                                    const num = recall.campaign_number || '';
                                    // Trust the resolved campaign-level status (admin overrides + per-user
                                    // overrides merged via get_tenant_campaigns + deduplicateCampaigns).
                                    // Fall back to the raw per-recall flag only when no resolution exists.
                                    const status = num ? campaignStatus[num] : null;
                                    if (status) {
                                      return status === 'Advance Notice';
                                    }
                                    return Boolean(recall.has_advance_notice_recalls);
                                  })()}
                                  variant='icon'
                                  size='sm'
                                />
                              </Badge>
                              <PausedCampaignIndicator
                                isPaused={
                                  recall.campaign_number
                                    ? campaignStatus[recall.campaign_number] === 'Paused'
                                    : false
                                }
                                pauseReason={
                                  recall.campaign_number
                                    ? campaignStatusReasons[recall.campaign_number]
                                    : undefined
                                }
                                variant='icon'
                                size='sm'
                              />
                            </div>
                          </TableCell>

                          <TableCell
                            style={{ width: `${columnWidths.description}px`, minWidth: 0 }}
                            className='p-3'
                          >
                            <div
                              className='text-sm truncate overflow-hidden max-w-full min-w-0'
                              title={recall.recall_title}
                            >
                              {recall.recall_title}
                            </div>
                          </TableCell>

                          <TableCell
                            style={{ width: `${columnWidths.year}px` }}
                            className='p-3 text-sm'
                          >
                            {recall.year}
                          </TableCell>

                          <TableCell style={{ width: `${columnWidths.make}px` }} className='p-3'>
                            <span className='text-sm truncate block' title={recall.make}>
                              {recall.make}
                            </span>
                          </TableCell>
                          <TableCell style={{ width: `${columnWidths.model}px` }} className='p-3'>
                            <span className='text-sm truncate block' title={recall.model}>
                              {recall.model}
                            </span>
                          </TableCell>

                          <TableCell style={{ width: `${columnWidths.dealer}px` }} className='p-3'>
                            <div className='text-sm truncate' title={recall.seller_name}>
                              {recall.seller_name}
                            </div>
                          </TableCell>

                          <TableCell style={{ width: `${columnWidths.address}px` }} className='p-3'>
                            <div className='text-sm truncate' title={recall.dealer_street}>
                              {recall.dealer_street || '-'}
                            </div>
                          </TableCell>

                          <TableCell style={{ width: `${columnWidths.city}px` }} className='p-3'>
                            <div className='text-sm truncate' title={recall.dealer_city}>
                              {recall.dealer_city || '-'}
                            </div>
                          </TableCell>

                          <TableCell
                            style={{ width: `${columnWidths.zip}px` }}
                            className='p-3 text-sm'
                          >
                            {recall.dealer_postal_code || '-'}
                          </TableCell>

                          <TableCell
                            style={{ width: `${columnWidths.route}px` }}
                            className='p-3 text-center'
                          >
                            {(() => {
                              const recallKey = `${recall.vin}-${recall.campaign_number}`;
                              const recallStatus = getRecallStatus(
                                recall.vin,
                                recall.campaign_number
                              );
                              const isSelected = selectedRecalls.has(recallKey);
                              const isLoading = addingToRoute.has(recallKey);
                              // Check campaign status first - user setting overrides database flag
                              // Handle case-insensitive campaign number matching
                              const campaignNumber = recall.campaign_number || '';
                              const getCampaignStatus = (
                                num: string
                              ): 'Available' | 'Advance Notice' | 'Paused' | null => {
                                if (!num) return null;
                                // Try exact match first
                                if (campaignStatus[num]) return campaignStatus[num];
                                // Try case-insensitive match
                                const normalized = num.toUpperCase();
                                const foundKey = Object.keys(campaignStatus).find(
                                  key => key.toUpperCase() === normalized
                                );
                                return foundKey ? campaignStatus[foundKey] : null;
                              };
                              // Trust the resolved campaign-level status: get_tenant_campaigns RPC
                              // + deduplicateCampaigns already merge admin/tenant overrides with the
                              // current user's own preferences. Only fall back to the raw per-recall
                              // flag when no campaign-level status is available.
                              let hasAdvanceNotice: boolean;
                              const resolvedCampaignStatus = getCampaignStatus(campaignNumber);
                              if (resolvedCampaignStatus !== null) {
                                hasAdvanceNotice = resolvedCampaignStatus === 'Advance Notice';
                              } else {
                                hasAdvanceNotice = Boolean(recall.has_advance_notice_recalls);
                              }
                              const isAssignedOrBeyond = [
                                'assigned',
                                'confirmed',
                                'in_progress',
                                'completed'
                              ].includes(recallStatus.status);

                              const actionError = actionErrors[recallKey];

                              return (
                                <div className='flex flex-col items-start gap-1'>
                                  <div className='relative flex items-center justify-start w-full'>
                                    <Button
                                      variant='ghost'
                                      size='icon'
                                      className='group h-7 w-7 hover:bg-accent hover:text-accent-foreground transition-colors'
                                      onClick={() => {
                                        if (!hasAdvanceNotice) {
                                          isSelected
                                            ? handleRemoveFromRoute(recall)
                                            : handleAddToRoute(recall);
                                        }
                                      }}
                                      disabled={isLoading || isAssignedOrBeyond || hasAdvanceNotice}
                                      title={
                                        hasAdvanceNotice
                                          ? "This recall is not serviceable yet - it's an advance notice. Parts or service procedures may not be available at this time."
                                          : isAssignedOrBeyond
                                            ? `Already ${recallStatus.status}${recallStatus.technicianName ? ` to ${recallStatus.technicianName}` : ''}`
                                            : isSelected
                                              ? 'Remove'
                                              : 'Add'
                                      }
                                    >
                                      {isLoading ? (
                                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary' />
                                      ) : hasAdvanceNotice ? (
                                        <AdvanceNoticeIndicator
                                          hasAdvanceNotice={hasAdvanceNotice}
                                          variant='icon'
                                          size='sm'
                                        />
                                      ) : isSelected ? (
                                        <Check className='h-4 w-4 text-green-600 dark:text-green-500 group-hover:text-green-700 dark:group-hover:text-green-400 transition-colors' />
                                      ) : isAssignedOrBeyond ? (
                                        <Check className='h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors' />
                                      ) : (
                                        <Plus className='h-4 w-4 text-primary group-hover:text-primary/90 dark:group-hover:text-primary transition-colors' />
                                      )}
                                    </Button>
                                    <Button
                                      variant='ghost'
                                      size='sm'
                                      onClick={e => handleRemoveRecallClick(recall, e)}
                                      className='absolute right-0 h-6 w-6 p-0 flex-shrink-0 hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors'
                                      title='Remove recall'
                                    >
                                      <X className='h-3.5 w-3.5' />
                                    </Button>
                                  </div>
                                  {actionError && (
                                    <InlineError
                                      message={actionError}
                                      className='text-xs max-w-[100px]'
                                    />
                                  )}
                                </div>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
        {/* Mobile Card View */}
        <div className='lg:hidden space-y-4 px-4'>
          {recalls.map((recall, index) => {
            const recallKey = `${recall.vin}-${recall.campaign_number}`;
            const isRemoved = removedRecalls.has(recallKey);
            const isFadingOut = fadingOutRecalls.has(recallKey);

            // Skip rendering if removed
            if (isRemoved) {
              return null;
            }

            return (
              <div
                key={`${recall.vin}-${recall.campaign_number}-${index}`}
                className={`border rounded-lg bg-card p-4 transition-opacity duration-300 ${
                  isFadingOut ? 'opacity-0' : 'opacity-100'
                }`}
              >
                {/* Header with VIN and Campaign */}
                <div className='flex items-center justify-between mb-3'>
                  <div className='flex items-center gap-2'>
                    <span className='font-mono text-sm font-medium'>{recall.vin}</span>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-6 w-6'
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(recall.vin);
                          showSuccessToast('VIN copied to clipboard!');
                        } catch (error) {
                          // Copy errors are non-critical, keep as toast
                          toast({
                            title: 'Error',
                            description: 'Failed to copy VIN to clipboard',
                            variant: 'destructive'
                          });
                        }
                      }}
                      title='Copy VIN'
                    >
                      <Copy className='h-3 w-3' />
                    </Button>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Badge
                      variant='outline'
                      className='text-xs shrink-0 bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30 dark:border-blue-400/30 inline-flex items-center gap-1'
                    >
                      <Megaphone className='h-3 w-3' />
                      {recall.campaign_number?.split('/')[0] || recall.campaign_number}
                      <AdvanceNoticeIndicator
                        hasAdvanceNotice={(() => {
                          const num = recall.campaign_number || '';
                          // Trust the resolved campaign-level status (admin overrides + per-user
                          // overrides merged via get_tenant_campaigns + deduplicateCampaigns).
                          // Fall back to the raw per-recall flag only when no resolution exists.
                          const status = num ? campaignStatus[num] : null;
                          if (status) {
                            return status === 'Advance Notice';
                          }
                          return Boolean(recall.has_advance_notice_recalls);
                        })()}
                        variant='icon'
                        size='sm'
                      />
                    </Badge>
                    <PausedCampaignIndicator
                      isPaused={
                        recall.campaign_number
                          ? campaignStatus[recall.campaign_number] === 'Paused'
                          : false
                      }
                      pauseReason={
                        recall.campaign_number
                          ? campaignStatusReasons[recall.campaign_number]
                          : undefined
                      }
                      variant='icon'
                      size='sm'
                    />
                  </div>
                </div>

                {/* Status Badge */}
                <div className='mb-3'>
                  {(() => {
                    const recallStatus = getRecallStatus(recall.vin, recall.campaign_number);
                    return (
                      <RecallStatusBadge
                        status={recallStatus.status}
                        technicianName={recallStatus.technicianName}
                        expectedVisitDate={recallStatus.expectedVisitDate}
                        size='sm'
                      />
                    );
                  })()}
                </div>

                {/* Vehicle Info */}
                <div className='mb-3'>
                  <div className='text-sm font-medium mb-1'>
                    {recall.year} {recall.make} {recall.model}
                  </div>
                  <div
                    className='text-xs text-muted-foreground truncate'
                    title={recall.recall_title}
                  >
                    {recall.recall_title}
                  </div>
                </div>

                {/* Dealer Info */}
                <div className='mb-3'>
                  <div className='text-sm font-medium mb-1'>{recall.seller_name}</div>
                  <div className='text-xs text-muted-foreground'>
                    {recall.dealer_street && `${recall.dealer_street}, `}
                    {recall.dealer_city && `${recall.dealer_city}, `}
                    {recall.dealer_postal_code}
                  </div>
                </div>

                {/* Actions */}
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    {(() => {
                      const recallKey = `${recall.vin}-${recall.campaign_number}`;
                      const recallStatus = getRecallStatus(recall.vin, recall.campaign_number);
                      const isSelected = selectedRecalls.has(recallKey);
                      const isLoading = addingToRoute.has(recallKey);
                      // Check campaign status first - user setting overrides database flag
                      const campaignNumber = recall.campaign_number || '';
                      const getCampaignStatus = (
                        num: string
                      ): 'Available' | 'Advance Notice' | 'Paused' | null => {
                        if (!num) return null;
                        if (campaignStatus[num]) return campaignStatus[num];
                        const normalized = num.toUpperCase();
                        const foundKey = Object.keys(campaignStatus).find(
                          key => key.toUpperCase() === normalized
                        );
                        return foundKey ? campaignStatus[foundKey] : null;
                      };
                      // Trust the resolved campaign-level status: get_tenant_campaigns RPC
                      // + deduplicateCampaigns already merge admin/tenant overrides with the
                      // current user's own preferences. Only fall back to the raw per-recall
                      // flag when no campaign-level status is available.
                      let hasAdvanceNotice: boolean;
                      const resolvedCampaignStatus = getCampaignStatus(campaignNumber);
                      if (resolvedCampaignStatus !== null) {
                        hasAdvanceNotice = resolvedCampaignStatus === 'Advance Notice';
                      } else {
                        hasAdvanceNotice = Boolean(recall.has_advance_notice_recalls);
                      }
                      const isAssignedOrBeyond = [
                        'assigned',
                        'confirmed',
                        'in_progress',
                        'completed'
                      ].includes(recallStatus.status);
                      const actionError = actionErrors[recallKey];

                      return (
                        <div className='flex flex-col gap-1.5'>
                          <div className='flex items-center gap-1'>
                            <Button
                              variant='ghost'
                              size='sm'
                              className='h-8'
                              onClick={() => {
                                if (!hasAdvanceNotice && !isAssignedOrBeyond) {
                                  isSelected
                                    ? handleRemoveFromRoute(recall)
                                    : handleAddToRoute(recall);
                                }
                              }}
                              disabled={isLoading || isAssignedOrBeyond || hasAdvanceNotice}
                              title={
                                hasAdvanceNotice
                                  ? "This recall is not serviceable yet - it's an advance notice. Parts or service procedures may not be available at this time."
                                  : isAssignedOrBeyond
                                    ? `Already ${recallStatus.status}${recallStatus.technicianName ? ` to ${recallStatus.technicianName}` : ''}`
                                    : isSelected
                                      ? 'Remove from route'
                                      : 'Add to route'
                              }
                            >
                              {isLoading ? (
                                <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-1' />
                              ) : isSelected ? (
                                <Check className='h-4 w-4 mr-1 text-green-600' />
                              ) : isAssignedOrBeyond ? (
                                <Check className='h-4 w-4 mr-1 text-muted-foreground' />
                              ) : hasAdvanceNotice ? (
                                <AdvanceNoticeIndicator
                                  hasAdvanceNotice={hasAdvanceNotice}
                                  variant='icon'
                                  size='sm'
                                  className='mr-1'
                                />
                              ) : (
                                <Plus className='h-4 w-4 mr-1' />
                              )}
                              {isSelected ? 'Added' : 'Route'}
                            </Button>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={e => handleRemoveRecallClick(recall, e)}
                              className='h-6 w-6 p-0 flex-shrink-0 hover:bg-destructive/10'
                              title='Remove recall'
                            >
                              <X className='h-3.5 w-3.5' />
                            </Button>
                          </div>
                          {actionError && <InlineError message={actionError} className='text-xs' />}
                        </div>
                      );
                    })()}
                  </div>
                  <div className='text-xs text-muted-foreground'>
                    Skill Level:{' '}
                    {recall.priority === 1 ? 'High' : recall.priority === 2 ? 'Medium' : 'Low'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Compact Pagination */}
        {effectivePaginationInfo && effectivePaginationInfo.totalPages > 1 && (
          <div className='flex items-center justify-center gap-1 py-2'>
            <Button
              variant='outline'
              size='sm'
              className='h-7 text-xs px-2'
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </Button>

            {generatePageNumbers().map(pageNum => (
              <Button
                key={pageNum}
                variant={pageNum === currentPage ? 'default' : 'outline'}
                size='sm'
                className='h-7 w-7 text-xs p-0'
                onClick={() => goToPage(pageNum)}
              >
                {pageNum}
              </Button>
            ))}

            <Button
              variant='outline'
              size='sm'
              className='h-7 text-xs px-2'
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === effectivePaginationInfo.totalPages}
            >
              Next
            </Button>
          </div>
        )}

        {/* Remove Recall Dialog */}
        <RemoveRecallDialog
          open={removalDialogOpen}
          onOpenChange={setRemovalDialogOpen}
          onConfirm={handleConfirmRemoval}
          recallTitle={recallToRemove?.recall_title}
          campaignNumber={recallToRemove?.campaign_number}
          vin={recallToRemove?.vin}
        />
      </div>
    </SecurityBoundary>
  );
};

export default RecallListPage;
